// validators.ts
import {
  allowAdditionalProperties,
  arrayOf,
  boolean,
  constant,
  date,
  number,
  object,
  oneOf,
  optional,
  string
} from "justus";
import { uuid } from "justus/extra/uuid";
var UUIDValidator = uuid;
var ean = string({ minLength: 13, maxLength: 13 });
var locale = oneOf("en", "de");
var isoDate = string({ pattern: /^\d{4}-\d{2}-\d{2}$/ });
var addressValidator = object({
  uuid: UUIDValidator,
  family_name: string,
  given_name: string,
  phone: string,
  street_name: string,
  house_number: string,
  postal_code: string,
  city: string,
  address_extra: optional(string),
  country: optional(string({ minLength: 2, maxLength: 2 }), "DE"),
  company: optional(string),
  vat: optional(string)
});
var productDataValidator = object({
  ean,
  quantity: number({ minimum: 1 })
});
var slotValidator = object({
  uuid: optional(UUIDValidator),
  date: string,
  slot: string,
  name: string,
  shipping_fee: number({ minimum: 0 }),
  delivery_provider_name: optional(string),
  cutoff: optional(date),
  picking_date: optional(isoDate)
});
var requestedSlotValidator = object({
  from: string,
  to: string
});
var priceData = object({
  unit_price: number({ multipleOf: 1 }),
  vat_rate: number({})
});
var productLineValidator = object({
  uuid: UUIDValidator,
  ean,
  juit_id: string,
  name: string({ minLength: 1 }),
  type: constant("physical"),
  quantity: number({ minimum: 1, multipleOf: 1 }),
  ...priceData,
  metadata: optional(object({ ...allowAdditionalProperties }))
});
var discountLineValidator = object({
  uuid: UUIDValidator,
  name: string({ minLength: 1 }),
  type: constant("discount"),
  quantity: constant(1),
  ...priceData,
  shipping_total: optional(number({ multipleOf: 1 })),
  shipping_total_net: optional(number({ multipleOf: 1 })),
  base_total: optional(number({ multipleOf: 1 })),
  base_total_net: optional(number({ multipleOf: 1 })),
  metadata: optional(object({ ...allowAdditionalProperties }))
});
var shippingLineValidator = object({
  uuid: optional(UUIDValidator),
  name: string({ minLength: 1 }),
  type: constant("shipping_fee"),
  quantity: constant(1),
  ...priceData,
  metadata: optional(object({ ...allowAdditionalProperties }))
});
var extraDishLineValidator = object({
  uuid: optional(UUIDValidator),
  ean,
  name: string({ minLength: 1 }),
  type: constant("extra_dish"),
  quantity: number({ minimum: 1, multipleOf: 1 }),
  ...priceData,
  metadata: optional(object({ ...allowAdditionalProperties }))
});
var lineValidator = oneOf(productLineValidator, discountLineValidator, shippingLineValidator, extraDishLineValidator);
var ProductError = object({
  subject: "product",
  ean,
  reason: "not_found"
});
var StockError = object({
  subject: "stock",
  ean,
  reason: "out_of_stock",
  available: number
});
var SlotError = object({
  subject: "slot",
  uuid,
  reason: oneOf("booked_out", "cutoff_passed", "postcode_mismatch")
});
var VoucherError = object({
  subject: "voucher",
  reason: oneOf("invalid", "expired", "used")
});
var OrderError = object({
  subject: "order",
  reason: "wrong_dish_quantity"
});
var PaymentMethodError = object({
  subject: "payment",
  reason: "method_not_available"
});
var ErrorValidator = oneOf(ProductError, StockError, SlotError, VoucherError, OrderError, PaymentMethodError);
var boxValidator = object({
  size: number({ minimum: 1, multipleOf: 1 }),
  vat: number({ minimum: 0, multipleOf: 1 }),
  total_net: number({ minimum: 0, multipleOf: 1 }),
  total: number({ minimum: 0, multipleOf: 1 }),
  price_per_dish: number({ minimum: 0, multipleOf: 1 }),
  price_per_dish_net: number({ minimum: 0, multipleOf: 1 })
});
var summaryValidator = object({
  quantity: number({ minimum: 0, multipleOf: 1 }),
  subtotal: number({ minimum: 0, multipleOf: 1 }),
  shipping_fee: number({ minimum: 0, multipleOf: 1 }),
  vat: number({ minimum: 0, multipleOf: 1 }),
  total_net: number({ minimum: 0, multipleOf: 1 }),
  total: number({ minimum: 0, multipleOf: 1 }),
  vat_breakdown: optional(arrayOf({ rate: number({ minimum: 0 }), vat: number({ minimum: 0, multipleOf: 1 }) }))
});
var cartValidator = object({
  uuid: UUIDValidator,
  created: date,
  modified: date,
  location_uuid: UUIDValidator,
  locale,
  lines: arrayOf(lineValidator),
  summary: summaryValidator,
  voucher_code: optional(string),
  user_uuid: optional(UUIDValidator),
  errors: optional(arrayOf(ErrorValidator)),
  box: optional(boxValidator),
  metadata: optional(object({ ...allowAdditionalProperties }))
});
var createCartValidator = object({
  location_uuid: optional(UUIDValidator),
  locale: optional(locale, "de"),
  lines: optional(arrayOf(productDataValidator)),
  voucher_code: optional(string),
  box_size: optional(number({ minimum: 1, multipleOf: 1 })),
  metadata: optional(object({ ...allowAdditionalProperties }))
});
var updateCartValidator = object({
  ...createCartValidator,
  lines: optional(arrayOf(productDataValidator), []),
  // default to no lines
  locale: optional(locale)
  // no default (preserve)
});
var preorderRequestValidator = object({
  location_uuid: UUIDValidator,
  lines: arrayOf(productDataValidator),
  locale,
  slot_uuid: optional(UUIDValidator),
  voucher_code: optional(string),
  shipping_address_uuid: optional(UUIDValidator),
  options: optional(object({ skip_inventory_check: optional(boolean), skip_sellable_check: optional(boolean) })),
  ...allowAdditionalProperties
});
var preorderResponseValidator = object({
  lines: arrayOf(lineValidator),
  summary: summaryValidator,
  errors: optional(arrayOf(ErrorValidator))
});
var utmValidator = object({
  campaign: optional(string),
  source: optional(string),
  medium: optional(string),
  id: optional(string),
  content: optional(string),
  ...allowAdditionalProperties
});
var metadata = {
  extra: optional(string),
  user_referral_code: optional(string),
  user_referred_by: optional(string),
  utm: optional(utmValidator),
  redelivery_for: optional(uuid),
  redelivered: optional(arrayOf(string)),
  order_for: optional(uuid),
  dishes_to_refund: optional(arrayOf({ ean: string, quantity: number })),
  ...allowAdditionalProperties
};
var createOrderValidator = object({
  lines: arrayOf(productDataValidator),
  location_uuid: uuid,
  locale: optional(locale, "de"),
  voucher_code: optional(string),
  billing_address_uuid: UUIDValidator,
  shipping_address_uuid: UUIDValidator,
  extra_message: optional(string),
  slot_uuid: uuid,
  redirect_url: optional(string),
  metadata: optional(metadata),
  cart_uuid: optional(uuid),
  pay_by_invoice: optional(boolean, false)
});
var orderStatus = oneOf("open", "authorized", "paid", "completed", "cancelled");
var createB2BOrderValidator = object({
  lines: arrayOf(productDataValidator),
  location_uuid: UUIDValidator,
  locale: optional(locale, "de"),
  voucher_code: optional(string),
  billing_address_uuid: UUIDValidator,
  shipping_address_uuid: UUIDValidator,
  extra_message: optional(string),
  redirect_url: optional(string),
  metadata: optional(metadata),
  slot: oneOf(requestedSlotValidator, slotValidator)
});
var updateB2BOrderValidator = object({
  lines: optional(arrayOf(productDataValidator)),
  billing_address_uuid: optional(uuid),
  shipping_address_uuid: optional(uuid),
  slot: optional(oneOf(requestedSlotValidator, slotValidator))
});
var b2BOpenOrderValidator = object({
  uuid: UUIDValidator,
  created: date,
  user_uuid: UUIDValidator,
  lines: arrayOf(lineValidator),
  summary: summaryValidator,
  location_uuid: UUIDValidator,
  tracking_id: string,
  locale: optional(locale, "de"),
  voucher_code: optional(string),
  billing_address: addressValidator,
  shipping_address: addressValidator,
  extra_message: optional(string),
  redirect_url: optional(string),
  metadata: optional(metadata),
  status: optional(constant("open"), "open"),
  slot: oneOf(requestedSlotValidator, slotValidator)
});
var orderValidator = object({
  uuid: UUIDValidator,
  created: date,
  user_uuid: UUIDValidator,
  location_uuid: UUIDValidator,
  slot: slotValidator,
  tracking_id: string,
  order_sequence: number({ minimum: 1 }),
  method: optional(string),
  channel: optional(oneOf("website", "app"), "website"),
  invoice_links: optional(arrayOf(string)),
  status: orderStatus,
  metadata: optional(metadata),
  locale,
  billing_address: addressValidator,
  shipping_address: addressValidator,
  lines: arrayOf(lineValidator),
  summary: summaryValidator,
  tag: oneOf("b2c", "b2b_small", "b2b_large", "manual")
});
var updateOrderValidator = object({
  shipping_address_uuid: optional(UUIDValidator),
  slot_uuid: optional(UUIDValidator)
});
var refundShippingFeeLine = {
  type: "shipping_fee",
  amount: optional(number({ minimum: 1 }))
};
var refundProductLine = {
  ean,
  type: "physical",
  quantity: optional(number({ minimum: 1 }))
};
var refundValidator = object({
  reason: optional(string({ maxLength: 512 })),
  lines: optional(arrayOf(oneOf(refundShippingFeeLine, refundProductLine)))
});
var NULL_UUID = optional(uuid, "00000000-0000-0000-0000-000000000000");
var productLine = object({
  ean,
  type: optional(constant("physical"), "physical"),
  unit_price: number({ minimum: 0 }),
  vat_rate: number({ minimum: 0 }),
  quantity: number({ minimum: 1 })
});
var createManualOrderValidator = object({
  user_uuid: UUIDValidator,
  lines: arrayOf(productLine),
  locale: optional(locale, "de"),
  location_uuid: NULL_UUID,
  billing_address_uuid: UUIDValidator,
  shipping_address_uuid: UUIDValidator,
  extra_message: optional(string),
  slot: optional(object({
    uuid: NULL_UUID,
    picking_date: optional(isoDate),
    date: optional(isoDate),
    carrier: optional(string),
    shipping_fee: optional(number({ minimum: 1 }))
  }), { uuid: "00000000-0000-0000-0000-000000000000" })
});
export {
  ErrorValidator,
  UUIDValidator,
  addressValidator,
  b2BOpenOrderValidator,
  boxValidator,
  cartValidator,
  createB2BOrderValidator,
  createCartValidator,
  createManualOrderValidator,
  createOrderValidator,
  discountLineValidator,
  ean,
  extraDishLineValidator,
  isoDate,
  lineValidator,
  locale,
  orderStatus,
  orderValidator,
  preorderRequestValidator,
  preorderResponseValidator,
  productDataValidator,
  productLineValidator,
  refundProductLine,
  refundShippingFeeLine,
  refundValidator,
  requestedSlotValidator,
  shippingLineValidator,
  slotValidator,
  summaryValidator,
  updateB2BOrderValidator,
  updateCartValidator,
  updateOrderValidator,
  utmValidator
};
//# sourceMappingURL=validators.mjs.map
